import { calculated, held, type EngineValue, type SourceType } from './evidence'
import { decimalToRational, integer, mul, type Rational } from './exact'

/**
 * 단위 정규화. 원문(raw)·정규화 값·기준 단위·확인 상태를 함께 둔다.
 * - 질량은 g, 부피는 ml, 개수는 ea 로 맞춘다.
 * - 숟갈·컵은 나라와 재료마다 다르고(1큰술 = 15ml 이지만 g 은 재료 밀도에 달림) 적당량·약간은 수치가 없다.
 *   그래서 확정하지 않고 needs_check 로 둔다. 숟갈·컵에는 참고 ml(suggestedMl)만 붙인다.
 * - 부피 → 질량은 원료별 밀도 근거가 있을 때만(convertToMass).
 */
export type Dimension = 'mass' | 'volume' | 'count'
export type BaseUnit = 'g' | 'ml' | 'ea'

/** 확정 단위표: 단위 → 차원과 기준 단위 배수. 대소문자·앞뒤 공백 무시 */
const UNIT_TABLE: Record<string, { dimension: Dimension; factor: number }> = {
  mg: { dimension: 'mass', factor: 0.001 },
  g: { dimension: 'mass', factor: 1 },
  kg: { dimension: 'mass', factor: 1000 },
  ml: { dimension: 'volume', factor: 1 },
  cc: { dimension: 'volume', factor: 1 },
  l: { dimension: 'volume', factor: 1000 },
  ea: { dimension: 'count', factor: 1 },
  개: { dimension: 'count', factor: 1 },
}
/**
 * 지금까지 저장된 결과의 배합비·원가 시나리오가 받아 온 단위(g·kg·ml·L). 이 네 단위 밖(mg·cc·ea)을 받기 시작하면
 * 옛 결과의 배합비 기준이 바뀌므로 formulationRatios 와 원가 시나리오는 이 목록만 쓴다.
 */
export const CORE_METRIC_UNITS = ['g', 'kg', 'ml', 'l'] as const
/** 가정용 계량: 참고 ml 만 있고 확정 값이 아니다 (한국·미국 표준 계량 기준) */
const HOUSEHOLD_ML: Record<string, number> = {
  큰술: 15,
  tbsp: 15,
  작은술: 5,
  tsp: 5,
  컵: 200,
  cup: 240,
}

const unitKey = (unit: string) => unit.trim().toLowerCase()

export interface NormalizedQuantity {
  raw: { amount: number | null; unit: string; text?: string }
  status: 'confirmed' | 'needs_check'
  dimension: Dimension | null
  baseUnit: BaseUnit | null
  /** 기준 단위 값. needs_check 면 null */
  value: Rational | null
  /** 가정용 계량의 참고 ml. 계산에 쓰지 않는다 */
  suggestedMl?: number
  issue?: 'AMOUNT_MISSING' | 'HOUSEHOLD_MEASURE' | 'AMBIGUOUS_UNIT'
}

export function normalizeQuantity(input: {
  amount: number | null
  unit: string
  /** 원문 그대로 ("간장 2큰술") — 보존만 한다 */
  raw?: string
}): NormalizedQuantity {
  const raw = { amount: input.amount, unit: input.unit, ...(input.raw ? { text: input.raw } : {}) }
  const key = unitKey(input.unit)
  const entry = UNIT_TABLE[key]
  const pending = (issue: NonNullable<NormalizedQuantity['issue']>, suggestedMl?: number) => ({
    raw,
    status: 'needs_check' as const,
    dimension: suggestedMl === undefined ? null : ('volume' as const),
    baseUnit: null,
    value: null,
    ...(suggestedMl === undefined ? {} : { suggestedMl }),
    issue,
  })
  if (input.amount === null || !Number.isFinite(input.amount) || input.amount < 0)
    return pending('AMOUNT_MISSING')
  if (!entry) {
    const household = HOUSEHOLD_ML[key]
    return household === undefined
      ? pending('AMBIGUOUS_UNIT')
      : pending('HOUSEHOLD_MEASURE', input.amount * household)
  }
  return {
    raw,
    status: 'confirmed',
    dimension: entry.dimension,
    baseUnit: entry.dimension === 'mass' ? 'g' : entry.dimension === 'volume' ? 'ml' : 'ea',
    value: mul(decimalToRational(input.amount), decimalToRational(entry.factor)),
  }
}

/**
 * 질량·부피 값(g 또는 ml). 저장된 결과와 같은 네 단위(CORE_METRIC_UNITS)만 받는다.
 * 원가 시나리오와 배합비가 쓰고, 그 밖의 단위는 null(보류)이다.
 */
export function metricQuantity(
  amount: number | null,
  unit: string,
): { dimension: 'mass' | 'volume'; value: Rational } | null {
  if (amount === null) return null
  const key = unitKey(unit)
  if (!(CORE_METRIC_UNITS as readonly string[]).includes(key)) return null
  return {
    dimension: key === 'g' || key === 'kg' ? 'mass' : 'volume',
    value: mul(decimalToRational(amount), integer(key === 'kg' || key === 'l' ? 1000 : 1)),
  }
}

/** 밀도 근거. 측정·공급사 규격서 값만 확정으로 본다 */
export interface Density {
  gPerMl: number
  source: SourceType
}

/** 질량(g)으로. 부피는 밀도가 있을 때만, 개수·확인 필요 단위는 보류 */
export function convertToMass(q: NormalizedQuantity, density?: Density): EngineValue<Rational> {
  if (q.status !== 'confirmed' || !q.value) return held('QUANTITY_UNCONFIRMED', 'quantity')
  if (q.dimension === 'mass') return calculated(q.value, [], ['calculated'])
  if (q.dimension === 'count') return held('COUNT_HAS_NO_MASS', 'quantity')
  if (!density || !Number.isFinite(density.gPerMl) || density.gPerMl <= 0)
    return held('DENSITY_REQUIRED', 'density')
  return calculated(mul(q.value, decimalToRational(density.gPerMl)), [], [density.source])
}

// ---- 단가 기준 단위 (RecipeCat shared/price-units 에서 옮겼다) ----

/** 시세·광고 단가의 기준 단위. 재료 단가 캐시(apps/api/src/pipeline/prices.ts)와 B2B 광고 단가가 같은 값을 쓴다 */
export const PRICE_BASE_UNITS = ['kg', 'L', 'ea'] as const
export type PriceBaseUnit = (typeof PRICE_BASE_UNITS)[number]

/** 재료 qty 단위(g/kg/ml/L/ea) → 가격 기준 단위(kg/L/ea) 환산 계수. 맞지 않으면 null */
export function unitFactor(ingredientUnit: string, priceUnit: PriceBaseUnit): number | null {
  const u = ingredientUnit.trim().toLowerCase()
  if (priceUnit === 'kg') {
    if (u === 'g') return 1 / 1000
    if (u === 'kg') return 1
    return null
  }
  if (priceUnit === 'L') {
    if (u === 'ml') return 1 / 1000
    if (u === 'l') return 1
    return null
  }
  return u === 'ea' ? 1 : null
}
